import { readJobSnapshot, jobResources } from './jobSnapshot';
import { useRef, useEffect, useState, useSyncExternalStore } from 'react';
import { formActivity } from './formActivity';
import { useSelector, useStore } from 'react-redux';
import { portalApi, fetchBackgroundData } from '../redux/api/portalApi';
import { replaceRealtimeSections } from '../redux/slices/jobDetailsSlice';
import { useRealtimeResource, useRealtimeState } from './RealtimeProvider';

export function readGeneralProjectSnapshot(payload, jobId) {
  const core = payload?.[String(jobId)] || payload;
  const value = core?.GeneralProjectInfo;
  const rows = Array.isArray(value) ? value : value ? [value] : [];
  return rows.length === 1 && String(rows[0]?.ID) === String(jobId) ? rows : null;
}

const editingField = () => Boolean(document.querySelector('.modal.show, [role="dialog"]') || document.activeElement?.matches('input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly]), [contenteditable="true"]'));

// JobHandler owns this adapter. Shared form hooks report drafts and saves;
// shared-hook forms and audited display sections refresh together.
export default function useGeneralProjectRealtime({ jobId, enabled, blocked, snapshotEditable = true }) {
  useSyncExternalStore(formActivity.subscribe, formActivity.snapshot);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const check = () => setFocused(editingField());
    const blur = () => { check(); };
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', blur);
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    check();
    return () => { observer.disconnect(); document.removeEventListener('focusin', check); document.removeEventListener('focusout', blur); };
  }, []);
  blocked = blocked || focused || formActivity.blocked();
  const store = useStore();
  const { mode } = useRealtimeState();
  const url = useSelector(state => state.apiUrls?.urls?.fullJob);
  const user = useSelector(state => state.user?.userDetails);
  const saving = state => Object.values(state[portalApi.reducerPath]?.mutations || {}).some(mutation => mutation.status === 'pending');
  const pendingSave = useSelector(saving);
  blocked = blocked || pendingSave;
  const loading = useSelector(state => Boolean(state.fullJobFetch?.loading || state.fullJobFetch?.syncing));
  const freshSince = useSelector(state => String(state.fullJobFetch?.lastJobId) === String(jobId) ? state.fullJobFetch?.freshSince || 0 : 0);
  const guard = useRef({});
  guard.current = { blocked: blocked || loading, enabled, jobId, url, user };
  useRealtimeResource({ enabled: enabled && ['domain', 'session'].includes(mode),
    freshSince,
    ownerKey: `job:${snapshotEditable ? 'editable' : 'readonly'}:${url}:${jobId}`,
    resources: [...jobResources, 'portal:tbd-finder'], blocked: blocked || loading,
    refresh: async ({ signal }) => {
      const valid = () => {
        const state = store.getState();
        const current = guard.current;
        return !signal.aborted && current.enabled && !current.blocked && !formActivity.blocked() && !editingField() && !saving(state)
          && (snapshotEditable ? state.readOnly?.isReadOnly !== true : state.readOnly?.isReadOnly === true)
          && current.jobId === jobId && current.url === url && current.user === user
          && String(state.jobId?.jobId) === String(jobId) && state.apiUrls?.urls?.fullJob === url
          && state.user?.userDetails === user && !state.fullJobFetch?.loading && !state.fullJobFetch?.syncing;
      };
      if (!valid()) return false;
      let superseded = false;
      const check = () => { if (!valid()) superseded = true; };
      const releaseStore = store.subscribe(check);
      const releaseDraft = formActivity.subscribe(check);
      document.addEventListener('focusin', check);
      const observer = new MutationObserver(check);
      observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
      try {
        const payload = await store.dispatch(fetchBackgroundData({ url: `${url}${url.includes('?') ? '&' : '?'}id=${encodeURIComponent(jobId)}`,
          signal, contract: { response: { keepOriginalKeys: true, includeMappedKeys: false } } }));
        if (superseded || !valid()) return false;
        const snapshot = readJobSnapshot(payload, jobId, snapshotEditable);
        if (!snapshot) return false;
        store.dispatch(replaceRealtimeSections(snapshot));
        return true;
      } finally {
        releaseStore(); releaseDraft();
        observer.disconnect(); document.removeEventListener('focusin', check);
      }
    }
  });
}
